// Generic, dependency-free CSV building + download helpers.

const escapeCsvCell = (value) => {
  if (value === null || value === undefined) return "";

  const stringValue = String(value);

  // Quote the cell when it contains a delimiter, a quote or a line break,
  // doubling any inner quotes as per RFC 4180.
  if (/[",\r\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Builds a CSV string from rows and a column definition.
 * @param {Array<Object>} rows
 * @param {Array<{ header: string, value: (row: Object, index: number) => any }>} columns
 * @param {number} [startIndex=0] added to each row's index, so a partial export
 *   (e.g. page 3 of the table) can keep the table's own row numbering.
 * @returns {string}
 */
export const buildCsv = (rows = [], columns = [], startIndex = 0) => {
  const headerLine = columns.map((column) => escapeCsvCell(column.header)).join(",");

  const dataLines = rows.map((row, index) =>
    columns.map((column) => escapeCsvCell(column.value(row, index + startIndex))).join(",")
  );

  return [headerLine, ...dataLines].join("\r\n");
};

/**
 * Triggers a browser download of the given CSV string.
 * A UTF-8 BOM is prepended so Excel opens accented characters correctly.
 */
export const downloadCsv = (filename, csv) => {
  const BOM = String.fromCharCode(0xfeff);
  const blob = new Blob([BOM, csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
